import React, { useState, useEffect, useRef } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { API_BASE, apiError } from "@/lib/api";
import { Logo, ServiceBar } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2, Fingerprint, Lock, KeyRound } from "lucide-react";
import { getQuickDevice, clearQuickDevice, setQuickDevice } from "@/lib/quickAccess";
import { QuickAccessSetupDialog } from "@/components/QuickAccessSetup";

export default function Login() {
  const { login, user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Modo de la pantalla: 'password' o 'pin'
  const [mode, setMode] = useState("password");
  const [greeting, setGreeting] = useState(null); // { first_name, device_id } | null

  // Password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // PIN
  const [pin, setPin] = useState("");
  const pinRef = useRef(null);

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal post-login para activar acceso rápido
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupFirstName, setSetupFirstName] = useState("");

  // Evita el race del useEffect de auto-redirect cuando el submit handler
  // ya decidió a dónde ir (PIN unlock → /welcome). El effect al detectar user
  // no-null volvería a mandar a /dashboard antes que corra el navigate explícito.
  const suppressAutoRedirect = useRef(false);

  // Detectar dispositivo confiado y hacer hint al montar
  useEffect(() => {
    const qd = getQuickDevice();
    if (!qd) return;
    (async () => {
      try {
        const r = await axios.post(`${API_BASE}/quick-access/hint`, { device_id: qd.device_id });
        if (r.data?.locked) {
          setError("Este dispositivo tiene el PIN bloqueado. Inicia sesión con contraseña.");
          setMode("password");
          return;
        }
        setGreeting({ first_name: r.data.first_name, device_id: qd.device_id });
        setMode("pin");
        setTimeout(() => pinRef.current?.focus(), 50);
      } catch (e) {
        // Dispositivo inválido / expirado → limpiar
        clearQuickDevice();
      }
    })();
  }, []);

  // Redirección si YA hay sesión al montar (one-shot). No dependemos del effect
  // reactivo porque compite con navegaciones explícitas de los handlers de submit.
  useEffect(() => {
    if (loading) return;
    if (user && !setupOpen && !suppressAutoRedirect.current) {
      navigate("/dashboard", { replace: true });
    }
    // Solo corre al terminar el primer refresh (loading→false), no en cada cambio de user
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (params.get("expired") === "1") setError("Tu sesión expiró. Por favor inicia sesión de nuevo.");
    if (params.get("qa_expired") === "1") setError("La confianza de este dispositivo expiró. Inicia sesión con contraseña.");
  }, [params]);

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const r = await login(email.trim(), password);
    setSubmitting(false);
    if (!r.ok) return setError(r.error);
    // Login OK. Si es admin y NO tiene dispositivo confiado registrado en este browser
    // → ofrecerlo con el modal (2b del PRD 9.10).
    const isAdmin = r.user?.role === "admin";
    const qd = getQuickDevice();
    if (isAdmin && !qd) {
      setSetupFirstName((r.user?.name || "").split(" ")[0] || "Admin");
      setSetupOpen(true);
      return;
    }
    navigate("/dashboard", { replace: true });
  };

  const onPinSubmit = async (e) => {
    e.preventDefault();
    if (!greeting) return;
    if (!/^\d{4,6}$/.test(pin)) { setError("PIN de 4 a 6 dígitos"); return; }
    setError(null); setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/quick-access/unlock`,
                                  { device_id: greeting.device_id, pin });
      // Guardar sesión igual que /auth/login
      localStorage.setItem("pe_token", r.data.access_token);
      localStorage.setItem("pe_user", JSON.stringify(r.data.user));
      setQuickDevice(r.data.device_id, r.data.first_name);
      // Suprime cualquier auto-redirect a /dashboard
      suppressAutoRedirect.current = true;
      // Sincroniza el contexto de auth ANTES de navegar; así Welcome ya ve user=admin
      await refresh();
      navigate("/welcome", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "PIN incorrecto";
      setError(detail);
      setPin("");
      if (status === 423 || status === 410 || status === 404) {
        // Bloqueado, expirado, o dispositivo revocado → forzar contraseña
        setMode("password");
        if (status === 404) clearQuickDevice();
      }
    } finally { setSubmitting(false); }
  };

  const switchToPassword = () => { setMode("password"); setError(null); setPin(""); };
  const switchToPin = () => {
    if (!greeting) return;
    setMode("pin"); setError(null);
    setTimeout(() => pinRef.current?.focus(), 50);
  };

  const forgetDevice = () => {
    clearQuickDevice();
    setGreeting(null);
    setMode("password");
    setError(null);
  };

  const onSetupDone = () => {
    setSetupOpen(false);
    navigate("/dashboard", { replace: true });
  };

  return (
    <>
    <div className="min-h-screen flex flex-col md:flex-row bg-[color:var(--pe-bg)]" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 relative"
           style={{
             backgroundImage:
               "linear-gradient(rgba(0,90,145,0.85), rgba(0,90,145,0.9)), url('https://images.unsplash.com/photo-1509390144018-eeaf65052242?crop=entropy&cs=srgb&fm=jpg&q=85')",
             backgroundSize: "cover", backgroundPosition: "center",
           }}>
        <div className="flex flex-col justify-between p-10 text-white w-full">
          <Logo height={64} dark />
          <div>
            <h1 className="text-4xl font-black tracking-tight">
              Cotiza, agenda y controla tu negocio eléctrico.
            </h1>
            <p className="mt-4 text-white/85 text-base max-w-md">
              La plataforma industrial de POWER & ENERGY.
              Cotizaciones seguras, edición con historial y control financiero.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {["Datos propios", "Auditoría total", "Respaldo 1-clic"].map((t) => (
                <div key={t} className="border border-white/25 rounded-md p-3">
                  <div className="text-[10px] tracking-widest text-white/60">SEGURIDAD</div>
                  <div className="text-sm font-bold mt-1">{t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] tracking-[0.18em] font-bold text-white/80">SOLUCIONES DE VANGUARDIA</div>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="md:hidden">
          <div className="bg-white px-6 py-5 flex items-center justify-between">
            <Logo height={44} />
            <span className="text-[10px] font-bold tracking-widest text-[color:var(--pe-primary)]">
              SOLUCIONES DE VANGUARDIA
            </span>
          </div>
          <ServiceBar />
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {mode === "pin" && greeting ? (
              <>
                <div className="pe-label mb-2 flex items-center gap-1.5">
                  <Fingerprint className="h-3.5 w-3.5" /> ACCESO RÁPIDO
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]"
                    data-testid="qa-greeting">
                  Hola {greeting.first_name}, <span className="text-slate-500 font-black">¿vamos por otra cotización?</span>
                </h2>
                <p className="text-slate-500 mt-3 text-sm">
                  Escribe tu PIN de 4 a 6 dígitos.
                </p>
                <form onSubmit={onPinSubmit} className="mt-6" data-testid="qa-pin-form">
                  <input
                    ref={pinRef}
                    type="password"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="qa-pin-input"
                    className="w-full text-center text-5xl tracking-[0.5em] font-black py-5 rounded-md border-2 border-[color:var(--pe-primary)]/20 focus:outline-none focus:border-[color:var(--pe-primary)] pe-numeric"
                    placeholder="••••"
                  />
                  <div className="text-center text-xs text-slate-500 mt-2">{pin.length}/6 dígitos</div>
                  {error && (
                    <div className="mt-4 flex gap-2 items-start text-sm bg-red-50 text-red-800 border border-red-200 rounded-md p-3"
                         data-testid="qa-error">
                      <AlertCircle size={16} className="shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}
                  <Button type="submit" disabled={submitting || pin.length < 4}
                          className="w-full h-12 pe-btn-primary text-white text-base mt-5"
                          data-testid="qa-unlock-submit">
                    {submitting ? <Loader2 className="animate-spin" size={18} /> : "Entrar"}
                  </Button>
                </form>
                <div className="mt-6 flex items-center justify-between text-xs">
                  <button type="button" onClick={switchToPassword} data-testid="qa-use-password"
                          className="text-[color:var(--pe-primary)] font-bold hover:underline flex items-center gap-1.5">
                    <KeyRound size={14} /> Usar contraseña
                  </button>
                  <button type="button" onClick={forgetDevice} data-testid="qa-forget-device"
                          className="text-slate-500 hover:text-slate-700 hover:underline">
                    No soy yo / olvidar este dispositivo
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="pe-label mb-2">ACCESO SEGURO</div>
                <h2 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]">
                  {greeting ? `Bienvenido, ${greeting.first_name}` : "Bienvenido"}
                </h2>
                <p className="text-slate-500 mt-2">
                  Solo usuarios autorizados. Si no tienes cuenta, contacta al administrador.
                </p>
                <form onSubmit={onPasswordSubmit} className="mt-8 space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="pe-label">Correo</Label>
                    <Input id="email" type="email" required autoComplete="email" autoFocus className="h-12"
                           value={email} onChange={(e) => setEmail(e.target.value)}
                           data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="pe-label">Contraseña</Label>
                    <Input id="password" type="password" required autoComplete="current-password" className="h-12"
                           value={password} onChange={(e) => setPassword(e.target.value)}
                           data-testid="login-password-input" />
                  </div>
                  {error && (
                    <div className="flex gap-2 items-start text-sm bg-red-50 text-red-800 border border-red-200 rounded-md p-3"
                         data-testid="login-error">
                      <AlertCircle size={16} className="shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}
                  <Button type="submit" disabled={submitting}
                          className="w-full h-12 pe-btn-primary text-white text-base"
                          data-testid="login-submit-button">
                    {submitting ? <Loader2 className="animate-spin" size={18} /> : "Iniciar sesión"}
                  </Button>
                </form>
                {greeting && (
                  <button type="button" onClick={switchToPin} data-testid="qa-back-to-pin"
                          className="mt-4 w-full flex items-center justify-center gap-2 text-[color:var(--pe-primary)] font-bold text-sm hover:underline">
                    <Fingerprint size={14} /> Volver a acceso rápido con PIN
                  </button>
                )}
                <div className="mt-6 text-xs text-slate-500">
                  ¿Recibiste un link de invitación? Ábrelo directamente para establecer tu contraseña.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    <QuickAccessSetupDialog
      open={setupOpen}
      onOpenChange={setSetupOpen}
      onDone={onSetupDone}
      firstName={setupFirstName}
    />
    </>
  );
}
